<template>
  <div class="google-map" ref="googleMap" v-if="!containsError()"></div>
</template>

<script>
import { Loader } from '@googlemaps/js-api-loader'
import { isEmpty, size } from 'lodash'
import { mapState } from 'vuex'
import ChartMixin from '@/mixins/ChartMixin'
import ChartDataService from '@/services/ChartDataService'
import MapConfig from '@/components/list-view-visualisation/MapConfig'
import ApiVersions from '@/constants/ApiVersions'

export default {
  name: 'MapChartWrapper',
  mixins: [ChartMixin],

  props: {
    latitude: String,
    longitude: String,
    location: String,
    measures: Array,
  },

  data() {
    return {
      google: null,
      map: null,
      isFullyRendered: false,
      mapData: [],
    }
  },
  computed: {
    ...mapState({
      geoCategories: (state) => state.ChartCategory.geoCategories,
      placeHolderMessage: (state) => state.ChartCategory.placeHolderMessage,
    }),
  },

  methods: {
    async loadMap(listId, latitude, longitude, location, measures, searchAndFilterParams) {
      try {
        this.setMapPlaceHolderMessage()
        if (!this.containsError()) {
          this.google = await new Loader({
            apiKey: MapConfig.getGoogleApiKey(),
            language: MapConfig.getLanguage(),
            region: MapConfig.getRegion(),
            version: ApiVersions.GOOGLE_MAPS,
          }).load()
          this.initializeMap()
          this.mapData = (
            await ChartDataService.getMapData(
              listId,
              latitude,
              longitude,
              location,
              measures,
              searchAndFilterParams,
            )
          ).data
          await this.emitLoaded()
          if (isEmpty(this.mapData.placeHolderMessage)) {
            this.updateMap(this.mapData)
          }
        }
      } catch (error) {
        const message = error.response ? error.response.data : error
        const notification = { type: 'error', message }
        await this.$store.dispatch('Notification/add', notification, { root: true })
      } finally {
        await this.emitLoaded()
        window.parent.postMessage('vue-loaded', window.location.origin)
      }
    },
    updateMap(mapData) {
      this.isFullyRendered = false
      this.map.data.addGeoJson(mapData.geoJson)
      this.map.data.setStyle((feature) => this.styleFeature(feature, mapData.min, mapData.max))

      const bounds = new this.google.maps.LatLngBounds(...mapData.bounds)
      this.map.fitBounds(bounds)
      this.map.panToBounds(bounds)

      const info = new this.google.maps.InfoWindow()
      this.map.data.addListener('mouseover', (event) => this.openInfoWindow(info, event))
      this.map.data.addListener('mouseout', () => this.closeInfoWindow(info))
      this.map.addListener('tilesloaded', async () => {
        this.isFullyRendered = true
      })
    },
    openInfoWindow(info, event) {
      const locationName = event.feature.getProperty('locationName')
      const value = event.feature.getProperty('value')
      const infoContent = locationName ? `${locationName}: <b>${value}</b>` : String(value)
      info.setContent(infoContent)
      info.setPosition(event.feature.getGeometry().get())
      info.setOptions({ pixelOffset: new this.google.maps.Size(0, -30) })
      info.open(this.map)
    },
    closeInfoWindow(info) {
      info.close(this.map)
    },
    initializeMap() {
      const mapConfig = {
        center: new this.google.maps.LatLng(0, 0),
        mapTypeId: 'roadmap',
        zoom: 2,
        streetViewControl: false,
        restriction: {
          latLngBounds: {
            north: 85,
            south: -85,
            west: -180,
            east: 180,
          },
        },
      }
      const mapContainer = this.$refs.googleMap
      this.map = new this.google.maps.Map(mapContainer, mapConfig)
    },
    prepareForScreenShot(disable) {
      if (this.map) {
        this.map.setOptions({ disableDefaultUI: disable })
      }
    },
    getMapsIcon(value, max, fraction, sameSize) {
      const yellow = [60, 100, 50]
      const red = [0, 100, 50]
      const sizeScale = sameSize ? 10 : 30
      const maxZIndex = 999999

      return {
        icon: {
          path: this.google.maps.SymbolPath.CIRCLE,
          scale: (value / max) * sizeScale,
          fillColor: this.color(yellow, red, fraction),
          fillOpacity: 0.5,
          strokeWeight: 0,
        },
        zIndex: maxZIndex - Math.floor(value),
      }
    },
    styleFeature(feature, min, max) {
      const value = parseFloat(feature.getProperty('value'))
      if (min === max) {
        return this.getMapsIcon(value, max, 1, min === max)
      }
      const fraction = (value - min) / (max - min)
      return this.getMapsIcon(value, max, fraction, min === max)
    },
    color(lowHsl, highHsl, fraction) {
      const color = []
      for (let i = 0; i < 3; i++) {
        // calculate color based on the fraction.
        color[i] = (highHsl[i] - lowHsl[i]) * fraction + lowHsl[i]
      }

      return `hsl(${color[0]},${color[1]}%,${color[2]}%)`
    },
    setMapPlaceHolderMessage() {
      this.$store.dispatch('setPlaceHolderMessage', '')
      if (!MapConfig.getGoogleApiKey()) {
        this.$store.dispatch('setPlaceHolderMessage', this.$t('message.invalidKeyMessage'))
      } else if (!this.containsRequiredColumns()) {
        this.$store.dispatch('setPlaceHolderMessage', this.$t('message.mappingColumnsRequired'))
      }
    },
    containsError() {
      return !isEmpty(this.placeHolderMessage)
    },
    containsRequiredColumns() {
      return size(this.geoCategories) > 1 || (this.isEmbedded && this.latitude && this.longitude)
    },
    loadData() {
      this.loadMap(
        this.listId,
        this.latitude,
        this.longitude,
        this.location,
        this.measures,
        this.getJsonObject(this.filterQuery),
      )
    },
    async emitLoaded() {
      if (!isEmpty(this.mapData.placeHolderMessage)) {
        this.$store.dispatch('setPlaceHolderMessage', this.mapData.placeHolderMessage)
      }
      await this.$emit('loaded', isEmpty(this.mapData.placeHolderMessage))
    },
  },
}
</script>
<style lang="postcss" scoped>
.google-map {
  @apply w-full h-full;
}
</style>
